"use client";

import React, { useEffect, useMemo, useState } from "react";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";
import { formatProfileName } from "../../utils/profileName";
import { SHIFT_TYPES } from "../../lib/shiftTypes";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

function getMonday(value) {
  const date = new Date(value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

export default function MySchedule() {
  const { isEnabled } = useFeatureFlags();
  const roleRequestsEnabled = isEnabled("feature.shift_role_requests");
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [requestShift, setRequestShift] = useState(null);
  const [requestPositions, setRequestPositions] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [requestStatus, setRequestStatus] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [shiftLoadError, setShiftLoadError] = useState("");

  useEffect(() => { void initialise(); }, []);

  async function initialise() {
    const { data } = await supabase.auth.getUser();
    const user = data.user;
    if (!user) return;
    setCurrentUser(user);
    const { data: staffProfile } = await supabase.from("profiles").select("id, full_name, name_prefix, position").eq("id", user.id).single();
    setProfile(staffProfile || null);
    await loadShiftData(user.id);
  }

  async function loadShiftData(userId = currentUser?.id) {
    const [shiftResult, allocationResult] = await Promise.all([
      supabase.from("shifts").select("*").order("time", { ascending: true }),
      userId ? supabase.from("shift_allocations").select(`id, shift_id, position_id, position:shift_positions!shift_allocations_position_id_fkey(id, position_name, section_name)`).eq("user_id", userId) : Promise.resolve({ data: [] }),
    ]);
    const firstError = shiftResult.error || allocationResult.error;
    setShiftLoadError(firstError?.message || "");
    if (shiftResult.data) setShifts(shiftResult.data);
    if (allocationResult.data) setAllocations(allocationResult.data);
  }

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => {
    const d = new Date(currentWeekStart); d.setDate(d.getDate() + index); return d;
  }), [currentWeekStart]);

  const allocationsByShift = useMemo(() => {
    const map = new Map();
    for (const allocation of allocations) {
      if (!map.has(allocation.shift_id)) map.set(allocation.shift_id, []);
      map.get(allocation.shift_id).push(allocation);
    }
    return map;
  }, [allocations]);

  function changeWeek(offset) {
    const next = new Date(currentWeekStart);
    next.setDate(next.getDate() + offset * 7);
    setCurrentWeekStart(next);
  }

  function shiftForDay(day) {
    return shifts.find((shift) => {
      const shiftAllocations = allocationsByShift.get(shift.id) || [];
      if (!shiftAllocations.length) return false;
      return new Date(shift.time).toDateString() === day.toDateString();
    });
  }

  async function getAccessToken() {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || "";
  }

  async function openAllocationRequest(shift) {
    if (shift.shift_status === "Cancelled") {
      alert(`This shift has been cancelled${shift.cancellation_reason ? `: ${shift.cancellation_reason}` : "."}`);
      return;
    }
    setRequestShift(shift);
    setRequestPositions([]);
    setRequestStatus("Loading available roles…");
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Your session has expired. Sign in again to claim shift roles.");
      const response = await fetch(`/api/shift-allocation-request?shiftId=${encodeURIComponent(shift.id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Unable to load shift roles");
      const positions = payload.positions || [];
      setRequestPositions(positions);
      setSelectedRoleIds((payload.myAllocations || []).map((item) => item.position_id));
      setRequestStatus(positions.length ? "" : "Management has not configured staffing roles for this shift yet.");
    } catch (error) {
      setRequestStatus(error.message);
      setRequestPositions([]);
      setSelectedRoleIds([]);
    }
  }

  function toggleRole(positionId, checked) {
    setSelectedRoleIds((current) => checked
      ? [...new Set([...current, positionId])]
      : current.filter((id) => id !== positionId));
  }

  async function saveRoleClaims() {
    if (!currentUser?.id || !requestShift?.id || requestSubmitting) return;
    const existing = allocationsByShift.get(requestShift.id) || [];
    if (selectedRoleIds.length === 0 && existing.length > 0 && !window.confirm("Release all of your roles for this shift?")) return;
    setRequestSubmitting(true);
    setRequestStatus("Saving your roles…");
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Your session has expired. Sign in again to continue.");
      const response = await fetch("/api/shift-allocation-request", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shiftId: requestShift.id, positionIds: selectedRoleIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Unable to save shift roles");
      setRequestStatus(payload.message || "Your roles have been claimed. No management approval is required.");
      await loadShiftData(currentUser.id);
      setTimeout(() => setRequestShift(null), 750);
    } catch (error) {
      setRequestStatus(error.message);
    } finally {
      setRequestSubmitting(false);
    }
  }

  const upcoming = shifts.filter((shift) => new Date(shift.time).getTime() >= Date.now()).slice(0, 12);
  const weekStartStr = weekDates[0].toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const weekEndStr = weekDates[6].toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <div className="nhsuk-width-container schedule-container">
          <section className="nhsuk-u-margin-top-6">
            <span className="pguh-public-kicker">Staffing</span>
            <h1 className="nhsuk-heading-l">My shifts & allocations</h1>
            <p className="nhsuk-body-m">Signed in as <strong>{profile ? formatProfileName(profile) : "Loading…"}</strong>. Claim every role you want for upcoming shifts. Roles are applied immediately with no Management approval.</p>
            {shiftLoadError && <div className="nhsuk-error-summary" role="alert"><h2 className="nhsuk-error-summary__title">Some shift information could not be loaded</h2><div className="nhsuk-error-summary__body"><p>{shiftLoadError}</p></div></div>}

            <div className="week-navigation nhsuk-u-margin-bottom-6">
              <button className="nhsuk-button nhsuk-button--secondary" onClick={() => changeWeek(-1)}>← Previous</button>
              <div className="pguh-schedule-week-label"><strong>{weekStartStr} – {weekEndStr}</strong></div>
              <button className="nhsuk-button nhsuk-button--secondary" onClick={() => setCurrentWeekStart(getMonday(new Date()))}>This week</button>
              <button className="nhsuk-button nhsuk-button--secondary" onClick={() => changeWeek(1)}>Next →</button>
            </div>

            <div className="overflow-x-auto">
              <table className="nhsuk-table nhsuk-table--striped nhsuk-u-width-full">
                <thead><tr><th>Date</th><th>Shift</th><th>Start</th><th>Your allocation</th></tr></thead>
                <tbody>{weekDates.map((day) => {
                  const shift = shiftForDay(day);
                  const dayAllocations = shift ? (allocationsByShift.get(shift.id) || []) : [];
                  return <tr key={day.toDateString()}>
                    <td>{day.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</td>
                    <td>{shift?.title || "–"}</td>
                    <td>{shift ? new Date(shift.time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "–"}</td>
                    <td>{dayAllocations.length ? dayAllocations.map((item) => item.position?.position_name).filter(Boolean).join(" · ") : "–"}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          </section>

          <section className="nhsuk-u-margin-top-7">
            <h2 className="nhsuk-heading-m">Upcoming shifts</h2>
            <div className="pguh-shift-request-grid">
              {upcoming.map((shift) => {
                const shiftAllocations = allocationsByShift.get(shift.id) || [];
                const cancelled = shift.shift_status === "Cancelled";
                return <article key={shift.id} className={`pguh-shift-request-card ${cancelled ? "is-cancelled" : ""}`}>
                  <div className="pguh-shift-request-card__top">
                    <span className="pguh-shift-type-badge">{SHIFT_TYPES[shift.shift_type]?.label || "Emergency Department Shift Allocation"}</span>
                    <h3>{shift.title}</h3>
                    <p>{new Date(shift.time).toLocaleString("en-GB")} · {shift.location || "Location TBC"}</p>
                  </div>
                  {cancelled ? <div className="pguh-allocation-state is-cancelled"><strong>Shift cancelled</strong><span>{shift.cancellation_reason || "This session will not take place."}</span></div>
                    : roleRequestsEnabled ? <>
                        {shiftAllocations.length > 0 && <div className="pguh-allocation-state is-approved"><strong>Your claimed roles</strong><span>{shiftAllocations.map((item) => item.position?.position_name).filter(Boolean).join(" · ")}</span></div>}
                        <button className="nhsuk-button" onClick={() => openAllocationRequest(shift)}>{shiftAllocations.length ? "Edit my roles" : "Claim roles"}</button>
                      </>
                    : <div className="pguh-allocation-state is-cancelled"><strong>Role claiming disabled</strong><span>Shift role claiming is currently unavailable.</span></div>}
                </article>;
              })}
            </div>
          </section>

          <div className="nhsuk-u-margin-bottom-8" />
        </div>
      </main>
      <Footer />

      {requestShift && roleRequestsEnabled && (
        <div className="pguh-allocation-modal-overlay" onClick={() => setRequestShift(null)}>
          <section
            className="pguh-allocation-request-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="allocation-request-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pguh-allocation-modal__header">
              <div>
                <span className="pguh-public-kicker">{SHIFT_TYPES[requestShift.shift_type]?.label || "Shift"}</span>
                <h2 id="allocation-request-title" className="nhsuk-heading-m">Claim shift roles</h2>
              </div>
              <button
                type="button"
                className="pguh-allocation-modal__close"
                aria-label="Close role claiming"
                onClick={() => setRequestShift(null)}
              >
                ×
              </button>
            </header>

            <div className="pguh-allocation-modal__body">
              <div className="pguh-allocation-modal__shift-summary">
                <strong>{requestShift.title}</strong>
                <span>{new Date(requestShift.time).toLocaleString("en-GB")}</span>
                {requestShift.location && <span>{requestShift.location}</span>}
              </div>

              <p className="nhsuk-body-s"><strong>Select every role you want to work.</strong> Saving this list immediately claims those roles; there is no approval queue.</p>
              <div className="pguh-role-claim-list">
                {requestPositions.map((position) => {
                  const wasMine = (allocationsByShift.get(requestShift.id) || []).some((item) => item.position_id === position.id);
                  const disabled = position.full && !wasMine;
                  const checked = selectedRoleIds.includes(position.id);
                  return <label key={position.id} className={`pguh-role-claim-option${checked ? " is-selected" : ""}${disabled ? " is-disabled" : ""}`}>
                    <input type="checkbox" checked={checked} disabled={disabled || requestSubmitting} onChange={(e) => toggleRole(position.id, e.target.checked)} />
                    <span><strong>{position.position_name}</strong><small>{wasMine ? "Currently claimed by you" : disabled ? "Full" : `${position.remaining} space${position.remaining === 1 ? "" : "s"} available`}</small></span>
                    <em>{position.allocated_count}/{position.capacity}</em>
                  </label>;
                })}
              </div>
              {requestStatus && <div className="pguh-shift-save-status" role="status">{requestStatus}</div>}
            </div>

            <footer className="pguh-allocation-modal__footer">
              <button className="nhsuk-button" disabled={requestSubmitting || requestPositions.length === 0} onClick={saveRoleClaims}>{requestSubmitting ? "Saving…" : selectedRoleIds.length ? `Save ${selectedRoleIds.length} role${selectedRoleIds.length === 1 ? "" : "s"}` : "Release all roles"}</button>
              <button className="nhsuk-button nhsuk-button--secondary" onClick={() => setRequestShift(null)}>Cancel</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
